import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";

import { AccountPicker } from "components/holdings/AccountPicker";
import { BucketAddAssetRow } from "components/buckets/BucketAddAssetRow";
import { BucketHistorySummary } from "components/buckets/BucketHistorySummary";
import { BucketMemberHistoryPanel } from "components/buckets/BucketMemberHistoryPanel";
import { BucketMembersList } from "components/buckets/BucketMembersList";
import { BucketToolbarActions } from "components/buckets/BucketToolbarActions";
import {
  BUCKET_DESCRIPTIONS,
  BUCKET_NUMBERS,
  formatMemberCounts,
  memberCounts,
} from "components/buckets/constants";
import { DeleteBucketConfirmModal } from "components/buckets/DeleteBucketConfirmModal";
import { useBucketActions } from "components/buckets/useBucketActions";
import { useTrading } from "features/store";
import {
  Bucket,
  BucketAsset,
  BucketNumberEnum,
  useListBucketsQuery,
} from "features/trading/buckets";

export default function Buckets() {
  const { currentAccountId } = useTrading();
  const [selectedNumber, setSelectedNumber] = useState<BucketNumberEnum>(
    BucketNumberEnum.BUCKET_1,
  );
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const { data: bucketsData, isFetching: isFetchingBuckets } =
    useListBucketsQuery(
      { account: currentAccountId as number },
      { skip: !currentAccountId },
    );
  // No data for the current account yet (first load or account switch) —
  // don't fall through to the "No Bucket" empty state while loading.
  const isBucketsLoading = isFetchingBuckets && !bucketsData;

  const bucketByNumber = useMemo(() => {
    const map = new Map<BucketNumberEnum, Bucket>();
    bucketsData?.results.forEach((bucket) => map.set(bucket.number, bucket));
    return map;
  }, [bucketsData]);

  const bucket = bucketByNumber.get(selectedNumber);
  const isBucket3 = selectedNumber === BucketNumberEnum.BUCKET_3;
  const actions = useBucketActions(currentAccountId, selectedNumber, bucket);

  // Clicking a member row opens its AccountHistory panel below the list;
  // clicking the same row again collapses it. Not on Bucket 3 — it doesn't
  // use AccountHistory (its report values are calculated dynamically).
  const [historyAssetId, setHistoryAssetId] = useState<number | null>(null);
  useEffect(() => {
    setHistoryAssetId(null);
  }, [currentAccountId, selectedNumber]);
  const handleMemberClick = useCallback((member: BucketAsset) => {
    setHistoryAssetId((previous) =>
      previous === member.asset ? null : member.asset,
    );
  }, []);
  const historyMember = bucket?.assets.find(
    (member) => member.asset === historyAssetId,
  );

  const handleDeleteBucket = async () => {
    const ok = await actions.removeBucket();
    if (ok) setIsConfirmDeleteOpen(false);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Card>
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          paddingX={2}
          paddingY={1.5}
        >
          <AccountPicker />
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={selectedNumber}
            onChange={(_, value) => {
              if (value !== null) setSelectedNumber(value);
            }}
          >
            {BUCKET_NUMBERS.map((number) => {
              const existing = bucketByNumber.get(number);
              return (
                <ToggleButton
                  key={number}
                  value={number}
                  sx={{ paddingX: 2, gap: 1, textTransform: "none" }}
                >
                  Bucket {number}
                  {existing && (
                    <Chip
                      size="small"
                      label={memberCounts(existing).included}
                      color={number === selectedNumber ? "primary" : "default"}
                    />
                  )}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
          <Box flexGrow={1} />
          <BucketToolbarActions
            bucket={bucket}
            actions={actions}
            onDeleteClick={() => setIsConfirmDeleteOpen(true)}
          />
        </Box>
      </Card>

      {isBucketsLoading ? (
        <Card>
          <Stack alignItems="center" sx={{ padding: 6 }}>
            <CircularProgress />
          </Stack>
        </Card>
      ) : bucket ? (
        <Card>
          <Box paddingX={2} paddingY={1.5}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography variant="h6" color="primary.main">
                Bucket {bucket.number}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={formatMemberCounts(bucket)}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" marginTop={0.5}>
              {BUCKET_DESCRIPTIONS[bucket.number]}
            </Typography>
            <Box marginTop={1}>
              <BucketHistorySummary bucket={bucket} actions={actions} />
            </Box>
          </Box>
          <Divider />
          {!isBucket3 && currentAccountId && (
            <Box paddingX={2} paddingTop={2}>
              <BucketAddAssetRow
                accountId={currentAccountId}
                actions={actions}
                maxWidth={440}
              />
            </Box>
          )}
          <Box padding={2}>
            <BucketMembersList
              bucket={bucket}
              actions={actions}
              selectedAssetId={historyAssetId}
              onMemberClick={isBucket3 ? undefined : handleMemberClick}
            />
          </Box>
        </Card>
      ) : (
        <Card>
          <Stack
            alignItems="center"
            justifyContent="center"
            gap={1.5}
            sx={{ padding: 6 }}
          >
            <Inventory2Outlined sx={{ fontSize: 44, color: "text.disabled" }} />
            <Typography variant="h6">
              No Bucket {selectedNumber} for this account
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              maxWidth={440}
            >
              {BUCKET_DESCRIPTIONS[selectedNumber]}
            </Typography>
            <Button
              variant="contained"
              sx={{ marginTop: 1 }}
              disabled={!currentAccountId || actions.isCreating}
              startIcon={
                actions.isCreating ? <CircularProgress size={16} /> : null
              }
              onClick={() => actions.create()}
            >
              Create Bucket {selectedNumber}
            </Button>
          </Stack>
        </Card>
      )}
      {historyMember && currentAccountId && (
        <BucketMemberHistoryPanel
          accountId={currentAccountId}
          member={historyMember}
        />
      )}
      <DeleteBucketConfirmModal
        open={isConfirmDeleteOpen}
        number={selectedNumber}
        bucket={bucket}
        confirming={actions.isDeleting}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleDeleteBucket}
      />
    </Box>
  );
}
